import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, Timestamp, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

interface Idea {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  title: string;
  description: string;
  category: 'auction' | 'challenge' | 'match' | 'giveaway' | 'charity';
  region: string;
  suggestedCelebrity: string;
  suggestedBrand: string;
  status: 'pending' | 'approved' | 'rejected' | 'in_review';
  votes: number;
  upvotes: number;
  downvotes: number;
  createdAt: Date;
  moderatedBy?: string;
  moderatedAt?: Date;
  moderatorNote?: string;
  venueId?: string; // For venue-specific ideas
  venueName?: string; // For display purposes
  type?: 'central' | 'venue'; // Central admin ideas vs venue-specific ideas
}

interface CampaignTemplate {
  id: string;
  name: string;
  type: 'auction' | 'challenge' | 'match' | 'giveaway' | 'charity';
  description: string;
  fields: string[];
  defaultSettings: Record<string, any>;
  active: boolean;
  createdAt: Date;
}

interface IdeaReport {
  id: string;
  ideaId: string;
  reportedBy: string;
  reporterName: string;
  reason: string;
  status: 'pending' | 'reviewed' | 'dismissed';
  reviewedBy?: string;
  createdAt: Date;
}

const IdeaBoxManagement: React.FC = () => {
  const { t } = useTranslation();
  const { user, isAdmin, isVenueOperator } = useAuth();
  const [activeTab, setActiveTab] = useState<'ideas' | 'templates' | 'analytics' | 'reports'>('ideas');
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [templates, setTemplates] = useState<CampaignTemplate[]>([]);
  const [reports, setReports] = useState<IdeaReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIdea, setSelectedIdea] = useState<Idea | null>(null);
  const [showIdeaModal, setShowIdeaModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<CampaignTemplate | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Template form state
  const [templateForm, setTemplateForm] = useState({
    name: '',
    type: 'challenge' as CampaignTemplate['type'],
    description: '',
    fields: '',
    active: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadIdeas(),
        loadTemplates(),
        loadReports()
      ]);
    } catch (error) {
      console.error('Error loading idea box data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadIdeas = async () => {
    try {
      let ideasData: Idea[] = [];

      if (isVenueOperator && !isAdmin) {
        // For venue operators, load only venue-specific ideas for their venue
        const selectedVenueId = localStorage.getItem('selectedVenueId');

        if (selectedVenueId) {
          const q = query(
            collection(db, 'venue_ideas'),
            where('venueId', '==', selectedVenueId),
            orderBy('createdAt', 'desc')
          );
          const snapshot = await getDocs(q);
          ideasData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate(),
            moderatedAt: doc.data().moderatedAt?.toDate(),
            type: 'venue'
          })) as Idea[];
        }
      } else {
        // For admins, load central ideas only
        const q = query(
          collection(db, 'user_ideas'),
          orderBy('createdAt', 'desc')
        );
        const snapshot = await getDocs(q);
        ideasData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate(),
          moderatedAt: doc.data().moderatedAt?.toDate(),
          type: 'central'
        })) as Idea[];
      }

      setIdeas(ideasData);
    } catch (error) {
      console.error('Error loading ideas:', error);
    }
  };

  const loadTemplates = async () => {
    const snapshot = await getDocs(collection(db, 'campaign_templates'));
    const templatesData = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate()
    })) as CampaignTemplate[];
    setTemplates(templatesData);
  };

  const loadReports = async () => {
    const snapshot = await getDocs(collection(db, 'idea_reports'));
    const reportsData = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate()
    })) as IdeaReport[];
    setReports(reportsData);
  };

  const handleModerateIdea = async (ideaId: string, newStatus: Idea['status'], note?: string) => {
    try {
      const collectionName = selectedIdea?.type === 'venue' ? 'venue_ideas' : 'user_ideas';

      await updateDoc(doc(db, collectionName, ideaId), {
        status: newStatus,
        moderatedBy: user?.displayName || user?.email,
        moderatedAt: Timestamp.now(),
        moderatorNote: note || ''
      });
      loadIdeas();
      setShowIdeaModal(false);
      setSelectedIdea(null);
    } catch (error) {
      console.error('Error moderating idea:', error);
    }
  };

  const handleDeleteIdea = async (ideaId: string, ideaType?: 'central' | 'venue') => {
    if (window.confirm(t('ideaBox.confirmDeleteIdea'))) {
      try {
        const collectionName = ideaType === 'venue' ? 'venue_ideas' : 'user_ideas';
        await deleteDoc(doc(db, collectionName, ideaId));
        loadIdeas();
      } catch (error) {
        console.error('Error deleting idea:', error);
      }
    }
  };

  const handleSaveTemplate = async () => {
    try {
      const templateData = {
        ...templateForm,
        fields: templateForm.fields.split(',').map(f => f.trim()).filter(f => f),
        defaultSettings: {},
        updatedAt: Timestamp.now()
      };

      if (editingTemplate) {
        await updateDoc(doc(db, 'campaign_templates', editingTemplate.id), templateData);
      } else {
        await addDoc(collection(db, 'campaign_templates'), {
          ...templateData,
          createdAt: Timestamp.now()
        });
      }

      setShowTemplateModal(false);
      setEditingTemplate(null);
      resetTemplateForm();
      loadTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (window.confirm(t('ideaBox.confirmDeleteTemplate'))) {
      try {
        await deleteDoc(doc(db, 'campaign_templates', templateId));
        loadTemplates();
      } catch (error) {
        console.error('Error deleting template:', error);
      }
    }
  };

  const handleReviewReport = async (reportId: string, status: 'reviewed' | 'dismissed') => {
    try {
      await updateDoc(doc(db, 'idea_reports', reportId), {
        status,
        reviewedBy: user?.displayName || user?.email
      });
      loadReports();
    } catch (error) {
      console.error('Error reviewing report:', error);
    }
  };

  const resetTemplateForm = () => {
    setTemplateForm({
      name: '',
      type: 'challenge',
      description: '',
      fields: '',
      active: true
    });
  };

  const getStatusColor = (status: Idea['status']) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500/20 text-yellow-400';
      case 'approved': return 'bg-green-500/20 text-green-400';
      case 'rejected': return 'bg-red-500/20 text-red-400';
      case 'in_review': return 'bg-blue-500/20 text-blue-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getCategoryIcon = (category: Idea['category']) => {
    switch (category) {
      case 'auction': return '🏆';
      case 'challenge': return '⚡';
      case 'match': return '🎯';
      case 'giveaway': return '🎁';
      case 'charity': return '💝';
      default: return '📋';
    }
  };

  const filteredIdeas = ideas.filter(idea => {
    const matchesStatus = filterStatus === 'all' || idea.status === filterStatus;
    const matchesCategory = filterCategory === 'all' || idea.category === filterCategory;
    const matchesSearch = idea.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      idea.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      idea.userName.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesCategory && matchesSearch;
  });

  const trendingIdeas = [...ideas]
    .filter(i => i.status === 'approved')
    .sort((a, b) => b.votes - a.votes)
    .slice(0, 5);

  const pendingReports = reports.filter(r => r.status === 'pending');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-[#B8B8C3]">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-16 lg:pt-0 pb-16 sm:pb-0">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-4xl font-bold text-gradient-cyan mb-2">
            💡 {isVenueOperator && !isAdmin ? 'Helyszín Ötletláda' : t('ideaBox.title')}
          </h2>
          <p className="text-[#B8B8C3]">
            {isVenueOperator && !isAdmin
              ? 'Kezeld a helyszínedre beérkezett ötleteket'
              : t('ideaBox.description')
            }
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[#31383D] overflow-x-auto">
        <button
          onClick={() => setActiveTab('ideas')}
          className={`px-4 sm:px-6 py-3 font-semibold transition-colors whitespace-nowrap text-sm sm:text-base ${activeTab === 'ideas'
              ? 'text-cyan-400 border-b-2 border-cyan-400'
              : 'text-[#B8B8C3] hover:text-white'
            }`}
        >
          {t('ideaBox.ideas')} {ideas.filter(i => i.status === 'pending').length > 0 && (
            <span className="ml-2 px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-xs">
              {ideas.filter(i => i.status === 'pending').length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('templates')}
          className={`px-4 sm:px-6 py-3 font-semibold transition-colors whitespace-nowrap text-sm sm:text-base ${activeTab === 'templates'
              ? 'text-cyan-400 border-b-2 border-cyan-400'
              : 'text-[#B8B8C3] hover:text-white'
            }`}
        >
          {t('ideaBox.templates')}
        </button>
        <button
          onClick={() => setActiveTab('analytics')}
          className={`px-4 sm:px-6 py-3 font-semibold transition-colors whitespace-nowrap text-sm sm:text-base ${activeTab === 'analytics'
              ? 'text-cyan-400 border-b-2 border-cyan-400'
              : 'text-[#B8B8C3] hover:text-white'
            }`}
        >
          {t('ideaBox.analytics')}
        </button>
        <button
          onClick={() => setActiveTab('reports')}
          className={`px-4 sm:px-6 py-3 font-semibold transition-colors whitespace-nowrap text-sm sm:text-base ${activeTab === 'reports'
              ? 'text-cyan-400 border-b-2 border-cyan-400'
              : 'text-[#B8B8C3] hover:text-white'
            }`}
        >
          {t('ideaBox.reports')} {pendingReports.length > 0 && (
            <span className="ml-2 px-2 py-1 bg-red-500/20 text-red-400 rounded-full text-xs">
              {pendingReports.length}
            </span>
          )}
        </button>
      </div>

      {/* Ideas Tab */}
      {activeTab === 'ideas' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="card-dark p-4 overflow-x-hidden">
            <div className="flex flex-col gap-3">
              <input
                type="text"
                placeholder={t('ideaBox.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-dark w-full"
              />
              <div className="flex gap-2 overflow-x-auto pb-2">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="input-dark flex-shrink-0 text-sm"
                >
                  <option value="all">{t('ideaBox.allStatuses')}</option>
                  <option value="pending">{t('ideaBox.status.pending')}</option>
                  <option value="in_review">{t('ideaBox.status.in_review')}</option>
                  <option value="approved">{t('ideaBox.status.approved')}</option>
                  <option value="rejected">{t('ideaBox.status.rejected')}</option>
                </select>
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="input-dark flex-shrink-0 text-sm"
                >
                  <option value="all">{t('ideaBox.allCategories')}</option>
                  <option value="auction">{t('ideaBox.category.auction')}</option>
                  <option value="challenge">{t('ideaBox.category.challenge')}</option>
                  <option value="match">{t('ideaBox.category.match')}</option>
                  <option value="giveaway">{t('ideaBox.category.giveaway')}</option>
                  <option value="charity">{t('ideaBox.category.charity')}</option>
                </select>
              </div>
            </div>
          </div>

          {/* Ideas List */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredIdeas.map(idea => (
              <div key={idea.id} className="card-dark p-4 sm:p-6">
                <div className="flex flex-col gap-3">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl sm:text-3xl flex-shrink-0">{getCategoryIcon(idea.category)}</span>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-lg sm:text-xl font-bold text-white mb-1 truncate">{idea.title}</h4>
                      <p className="text-[#B8B8C3] text-xs sm:text-sm mb-2 line-clamp-2">{idea.description}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(idea.status)}`}>
                      {t(`ideaBox.status.${idea.status}`)}
                    </span>
                    <span className="px-2 py-1 rounded-full text-xs font-semibold bg-purple-500/20 text-purple-400">
                      {getCategoryIcon(idea.category)} {idea.category}
                    </span>
                    {idea.region && (
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-500/20 text-blue-400">
                        📍 {idea.region}
                      </span>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-3 text-xs sm:text-sm text-[#B8B8C3]">
                    <div className="flex items-center gap-1">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className="truncate">{idea.userName}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {idea.createdAt.toLocaleDateString()}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-green-400">↑ {idea.upvotes}</span>
                      <span className="text-red-400">↓ {idea.downvotes}</span>
                      <span className="text-cyan-400 font-semibold">{idea.votes}</span>
                    </div>
                  </div>

                  {(idea.suggestedCelebrity || idea.suggestedBrand) && (
                    <div className="flex flex-col gap-1 text-xs sm:text-sm text-[#B8B8C3]">
                      {idea.suggestedCelebrity && (
                        <div className="truncate">
                          ⭐ {t('ideaBox.suggestedCelebrity')}: <span className="text-white">{idea.suggestedCelebrity}</span>
                        </div>
                      )}
                      {idea.suggestedBrand && (
                        <div className="truncate">
                          🏢 {t('ideaBox.suggestedBrand')}: <span className="text-white">{idea.suggestedBrand}</span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setSelectedIdea(idea);
                        setShowIdeaModal(true);
                      }}
                      className="btn-secondary text-xs sm:text-sm flex-1 sm:flex-none sm:min-w-[120px]"
                    >
                      {t('ideaBox.moderate')}
                    </button>
                    <button
                      onClick={() => handleDeleteIdea(idea.id, idea.type)}
                      className="px-3 sm:px-4 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors text-xs sm:text-sm flex-1 sm:flex-shrink-0 sm:min-w-[120px] border-2 border-red-500/30 hover:border-red-500/50"
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredIdeas.length === 0 && (
            <div className="card-dark p-12 text-center">
              <div className="text-6xl mb-4">💡</div>
              <h3 className="text-xl font-bold text-white mb-2">{t('ideaBox.noIdeas')}</h3>
              <p className="text-[#B8B8C3]">{t('ideaBox.noIdeasDesc')}</p>
            </div>
          )}
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-2xl font-bold text-white">{t('ideaBox.templateManagement')}</h3>
            <button
              onClick={() => {
                resetTemplateForm();
                setEditingTemplate(null);
                setShowTemplateModal(true);
              }}
              className="btn-primary flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('ideaBox.newTemplate')}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map(template => (
              <div key={template.id} className="card-dark p-6">
                <div className="flex justify-between items-start mb-4">
                  <h4 className="text-xl font-bold text-white">{template.name}</h4>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${template.active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                    }`}>
                    {template.active ? t('ideaBox.active') : t('ideaBox.inactive')}
                  </span>
                </div>

                <p className="text-[#B8B8C3] text-sm mb-4">{template.description}</p>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-[#B8B8C3]">{t('ideaBox.type')}:</span>
                    <span className="text-white font-semibold">{t(`ideaBox.category.${template.type}`)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-[#B8B8C3]">{t('ideaBox.fields')}:</span>
                    <span className="text-white">{template.fields.length}</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setEditingTemplate(template);
                      setTemplateForm({
                        name: template.name,
                        type: template.type,
                        description: template.description,
                        fields: template.fields.join(', '),
                        active: template.active
                      });
                      setShowTemplateModal(true);
                    }}
                    className="flex-1 btn-secondary text-sm"
                  >
                    {t('common.edit')}
                  </button>
                  <button
                    onClick={() => handleDeleteTemplate(template.id)}
                    className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors text-sm"
                  >
                    {t('common.delete')}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {templates.length === 0 && (
            <div className="card-dark p-12 text-center">
              <div className="text-6xl mb-4">📋</div>
              <h3 className="text-xl font-bold text-white mb-2">{t('ideaBox.noTemplates')}</h3>
              <p className="text-[#B8B8C3] mb-4">{t('ideaBox.noTemplatesDesc')}</p>
              <button
                onClick={() => {
                  resetTemplateForm();
                  setShowTemplateModal(true);
                }}
                className="btn-primary"
              >
                {t('ideaBox.createFirstTemplate')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="space-y-4">
          <h3 className="text-2xl font-bold text-white mb-4">{t('ideaBox.votingAnalytics')}</h3>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="card-dark p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#B8B8C3] text-sm">{t('ideaBox.totalIdeas')}</span>
                <span className="text-3xl">💡</span>
              </div>
              <p className="text-3xl font-bold text-white">{ideas.length}</p>
            </div>

            <div className="card-dark p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#B8B8C3] text-sm">{t('ideaBox.pendingReview')}</span>
                <span className="text-3xl">⏳</span>
              </div>
              <p className="text-3xl font-bold text-yellow-400">{ideas.filter(i => i.status === 'pending').length}</p>
            </div>

            <div className="card-dark p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#B8B8C3] text-sm">{t('ideaBox.approved')}</span>
                <span className="text-3xl">✅</span>
              </div>
              <p className="text-3xl font-bold text-green-400">{ideas.filter(i => i.status === 'approved').length}</p>
            </div>

            <div className="card-dark p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#B8B8C3] text-sm">{t('ideaBox.totalVotes')}</span>
                <span className="text-3xl">🗳️</span>
              </div>
              <p className="text-3xl font-bold text-cyan-400">{ideas.reduce((sum, i) => sum + i.votes, 0)}</p>
            </div>
          </div>

          {/* Trending Ideas */}
          <div className="card-dark p-6">
            <h4 className="text-xl font-bold text-white mb-4">🔥 {t('ideaBox.trendingIdeas')}</h4>
            <div className="space-y-3">
              {trendingIdeas.map((idea, index) => (
                <div key={idea.id} className="flex items-center gap-4 p-4 bg-[#15191E] rounded-lg">
                  <div className="text-2xl font-bold text-cyan-400">#{index + 1}</div>
                  <div className="flex-1">
                    <h5 className="text-white font-semibold">{idea.title}</h5>
                    <p className="text-sm text-[#B8B8C3]">{idea.userName}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-cyan-400">{idea.votes}</p>
                    <p className="text-xs text-[#B8B8C3]">{t('ideaBox.votes')}</p>
                  </div>
                </div>
              ))}
            </div>

            {trendingIdeas.length === 0 && (
              <p className="text-center text-[#B8B8C3] py-8">{t('ideaBox.noTrendingIdeas')}</p>
            )}
          </div>
        </div>
      )}

      {/* Reports Tab */}
      {activeTab === 'reports' && (
        <div className="space-y-4">
          <h3 className="text-2xl font-bold text-white mb-4">{t('ideaBox.reportManagement')}</h3>

          <div className="space-y-3">
            {reports.map(report => (
              <div key={report.id} className="card-dark p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="text-lg font-bold text-white">{t('ideaBox.reportedIdea')}</h4>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${report.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          report.status === 'reviewed' ? 'bg-green-500/20 text-green-400' :
                            'bg-gray-500/20 text-gray-400'
                        }`}>
                        {t(`ideaBox.reportStatus.${report.status}`)}
                      </span>
                    </div>

                    <p className="text-[#B8B8C3] text-sm mb-3">{report.reason}</p>

                    <div className="flex items-center gap-4 text-sm text-[#B8B8C3]">
                      <div className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        {report.reporterName}
                      </div>
                      <div className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {report.createdAt.toLocaleDateString()}
                      </div>
                    </div>
                  </div>

                  {report.status === 'pending' && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleReviewReport(report.id, 'reviewed')}
                        className="btn-primary text-sm"
                      >
                        {t('ideaBox.markReviewed')}
                      </button>
                      <button
                        onClick={() => handleReviewReport(report.id, 'dismissed')}
                        className="btn-secondary text-sm"
                      >
                        {t('ideaBox.dismiss')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {reports.length === 0 && (
            <div className="card-dark p-12 text-center">
              <div className="text-6xl mb-4">🚩</div>
              <h3 className="text-xl font-bold text-white mb-2">{t('ideaBox.noReports')}</h3>
              <p className="text-[#B8B8C3]">{t('ideaBox.noReportsDesc')}</p>
            </div>
          )}
        </div>
      )}

      {/* Idea Moderation Modal */}
      {showIdeaModal && selectedIdea && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card-dark p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-white mb-6">{t('ideaBox.moderateIdea')}</h3>

            <div className="space-y-6 mb-6">
              {/* Highlighted Idea Title and Description */}
              <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border-l-4 border-cyan-400 p-4 rounded-lg">
                <h4 className="text-2xl font-bold text-cyan-400 mb-3">{selectedIdea.title}</h4>
                <p className="text-white text-base leading-relaxed">{selectedIdea.description}</p>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <span className="block text-sm text-[#B8B8C3] mb-1">{t('ideaBox.submittedBy')}:</span>
                  <p className="text-white font-semibold">{selectedIdea.userName}</p>
                </div>
                <div>
                  <span className="block text-sm text-[#B8B8C3] mb-1">Típus:</span>
                  <p className="text-white font-semibold">{selectedIdea.category}</p>
                </div>
                <div>
                  <span className="block text-sm text-[#B8B8C3] mb-1">{t('ideaBox.region')}:</span>
                  <p className="text-white font-semibold">{selectedIdea.region || '-'}</p>
                </div>
                <div>
                  <span className="block text-sm text-[#B8B8C3] mb-1">{t('ideaBox.votes')}:</span>
                  <p className="text-cyan-400 font-bold text-lg">{selectedIdea.votes}</p>
                </div>
              </div>

              {/* Optional Fields */}
              {(selectedIdea.suggestedCelebrity || selectedIdea.suggestedBrand) && (
                <div className="space-y-3">
                  {selectedIdea.suggestedCelebrity && (
                    <div>
                      <span className="block text-sm text-[#B8B8C3] mb-1">{t('ideaBox.suggestedCelebrity')}:</span>
                      <p className="text-white font-semibold">{selectedIdea.suggestedCelebrity}</p>
                    </div>
                  )}
                  {selectedIdea.suggestedBrand && (
                    <div>
                      <span className="block text-sm text-[#B8B8C3] mb-1">{t('ideaBox.suggestedBrand')}:</span>
                      <p className="text-white font-semibold">{selectedIdea.suggestedBrand}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <button
                onClick={() => handleModerateIdea(selectedIdea.id, 'approved')}
                className="bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-lg transition-colors font-semibold text-sm flex items-center justify-center gap-1"
              >
                <span>✅</span>
                <span>Jóváhagyás</span>
              </button>
              <button
                onClick={() => handleModerateIdea(selectedIdea.id, 'in_review')}
                className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-lg transition-colors font-semibold text-sm flex items-center justify-center gap-1"
              >
                <span>🔍</span>
                <span>Vizsgálat</span>
              </button>
              <button
                onClick={() => handleModerateIdea(selectedIdea.id, 'rejected')}
                className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-lg transition-colors font-semibold text-sm flex items-center justify-center gap-1"
              >
                <span>❌</span>
                <span>Elutasítás</span>
              </button>
              <button
                onClick={() => {
                  setShowIdeaModal(false);
                  setSelectedIdea(null);
                }}
                className="btn-secondary"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card-dark p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-white mb-6">
              {editingTemplate ? t('ideaBox.editTemplate') : t('ideaBox.newTemplate')}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  {t('ideaBox.templateName')}
                </label>
                <input
                  type="text"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  className="input-dark w-full"
                  placeholder={t('ideaBox.templateNamePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  {t('ideaBox.type')}
                </label>
                <select
                  value={templateForm.type}
                  onChange={(e) => setTemplateForm({ ...templateForm, type: e.target.value as CampaignTemplate['type'] })}
                  className="input-dark w-full"
                >
                  <option value="auction">{t('ideaBox.category.auction')}</option>
                  <option value="challenge">{t('ideaBox.category.challenge')}</option>
                  <option value="match">{t('ideaBox.category.match')}</option>
                  <option value="giveaway">{t('ideaBox.category.giveaway')}</option>
                  <option value="charity">{t('ideaBox.category.charity')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  {t('ideaBox.description')}
                </label>
                <textarea
                  value={templateForm.description}
                  onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                  className="input-dark w-full"
                  rows={3}
                  placeholder={t('ideaBox.templateDescPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  {t('ideaBox.fields')}
                </label>
                <input
                  type="text"
                  value={templateForm.fields}
                  onChange={(e) => setTemplateForm({ ...templateForm, fields: e.target.value })}
                  className="input-dark w-full"
                  placeholder={t('ideaBox.fieldsPlaceholder')}
                />
                <p className="text-xs text-[#B8B8C3] mt-1">{t('ideaBox.fieldsHint')}</p>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="active"
                  checked={templateForm.active}
                  onChange={(e) => setTemplateForm({ ...templateForm, active: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="active" className="text-sm text-[#B8B8C3]">
                  {t('ideaBox.activeTemplate')}
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={handleSaveTemplate} className="btn-primary flex-1">
                {t('common.save')}
              </button>
              <button
                onClick={() => {
                  setShowTemplateModal(false);
                  setEditingTemplate(null);
                  resetTemplateForm();
                }}
                className="btn-secondary flex-1"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IdeaBoxManagement;
